import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Edit2, Trash2, Save, X, AlertCircle, ArrowLeft, Search, Key, Users as UsersIcon, Shield, ChevronDown, ChevronUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import Button from '../components/Button';
import LoadingSpinner from '../components/LoadingSpinner';
import ConfirmationDialog from '../components/ConfirmationDialog';
import { useUsers, useCreateUser, useUpdateUser, useDeleteUser, useResetPassword, User, CreateUserRequest, UpdateUserRequest } from '../hooks/useUsers';
import { useRoles, useCreateRole, useUpdateRole, useDeleteRole, Role, CreateRoleRequest, UpdateRoleRequest } from '../hooks/useRoles';
import toast from 'react-hot-toast';
import { roleLabel, PORTAL_ASSIGNABLE_ROLE_NAMES, normalizeRole, isAdmin } from '../utils/roles';
import { usePortalBranches } from '../hooks/usePortalWorkflow';

const Users: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newUser, setNewUser] = useState<CreateUserRequest>({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    role: '',
    branchIds: [],
  });

  const [deleteDialog, setDeleteDialog] = useState({
    isOpen: false,
    user: null as User | null
  });
  const [resetPasswordDialog, setResetPasswordDialog] = useState({
    isOpen: false,
    user: null as User | null,
    newPassword: ''
  });

  // Role management state
  const [showRolesSection, setShowRolesSection] = useState(false);
  const [showAddRoleForm, setShowAddRoleForm] = useState(false);
  const [editingRoleId, setEditingRoleId] = useState<string | null>(null);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [newRole, setNewRole] = useState<CreateRoleRequest>({
    name: '',
    description: ''
  });
  const [deleteRoleDialog, setDeleteRoleDialog] = useState({
    isOpen: false,
    role: null as Role | null
  });
  const [roleSearchTerm, setRoleSearchTerm] = useState('');

  // API hooks
  const { data, isLoading, error } = useUsers(searchTerm, selectedRole);
  const createMutation = useCreateUser();
  const updateMutation = useUpdateUser();
  const deleteMutation = useDeleteUser();
  const resetPasswordMutation = useResetPassword();

  // Role hooks - fetch all roles (search is only used when role management section is open)
  const { data: rolesData, isLoading: rolesLoading } = useRoles(showRolesSection ? roleSearchTerm : '');
  const { data: portalBranches = [], isLoading: branchesLoading } = usePortalBranches();
  const allRoles = useMemo(() => rolesData?.roles ?? [], [rolesData?.roles]);
  const assignableRoles = useMemo(() => {
    const byName = new Map(allRoles.map((r) => [normalizeRole(r.name), r]));
    return PORTAL_ASSIGNABLE_ROLE_NAMES.map((name) => {
      const fromApi = byName.get(name);
      if (fromApi) return fromApi;
      return {
        id: name,
        name,
        description: null,
        createdAt: '',
        updatedAt: '',
      };
    });
  }, [allRoles]);
  const defaultRoleName = 'video_recorder';
  const createRoleMutation = useCreateRole();
  const updateRoleMutation = useUpdateRole();
  const deleteRoleMutation = useDeleteRole();

  const users = data?.users || [];

  // Set default role when roles are loaded
  useEffect(() => {
    if (!newUser.role) {
      setNewUser((prev) => ({ ...prev, role: defaultRoleName }));
    }
  }, [defaultRoleName, newUser.role]);

  const handleAdd = async () => {
    if (!newUser.email || !newUser.password || !newUser.firstName || !newUser.lastName || !newUser.role) {
      toast.error('Please fill in all required fields including role');
      return;
    }

    const role = normalizeRole(newUser.role);
    const payload: CreateUserRequest = {
      ...newUser,
      branchIds: isAdmin(role) ? [] : newUser.branchIds ?? [],
    };

    try {
      await createMutation.mutateAsync(payload);
      toast.success('User created successfully');
      setShowAddForm(false);
      setNewUser({
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        role: defaultRoleName,
        branchIds: [],
      });
    } catch (error: any) {
      toast.error(error.response?.data?.error?.message || 'Failed to create user');
    }
  };

  const toggleBranchId = (ids: number[], branchId: number) =>
    ids.includes(branchId) ? ids.filter((id) => id !== branchId) : [...ids, branchId];

  const formatBranchSummary = (branchIds: number[]) => {
    if (!branchIds.length) return 'All branches';
    const names = portalBranches.filter((b) => branchIds.includes(b.id)).map((b) => b.name);
    return names.length > 0 ? names.join(', ') : `${branchIds.length} branch(es)`;
  };

  const handleEdit = (user: User) => {
    setEditingId(user.id);
    setEditingUser({ ...user, branchIds: user.branchIds ?? [] });
  };

  const handleSaveEdit = async () => {
    if (!editingUser) return;

    const role = normalizeRole(editingUser.role);
    const updateData: UpdateUserRequest = {
      email: editingUser.email,
      firstName: editingUser.firstName,
      lastName: editingUser.lastName,
      role: editingUser.role,
      branchIds: isAdmin(role) ? [] : editingUser.branchIds ?? [],
    };

    try {
      await updateMutation.mutateAsync({ id: editingUser.id, data: updateData });
      toast.success('User updated successfully');
      setEditingId(null);
      setEditingUser(null);
    } catch (error: any) {
      toast.error(error.response?.data?.error?.message || 'Failed to update user');
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingUser(null);
  };

  const handleDelete = (user: User) => {
    setDeleteDialog({
      isOpen: true,
      user
    });
  };

  const confirmDelete = async () => {
    if (!deleteDialog.user) return;
    
    try {
      await deleteMutation.mutateAsync(deleteDialog.user.id);
      toast.success('User deleted successfully');
      setDeleteDialog({ isOpen: false, user: null });
    } catch (error: any) {
      toast.error(error.response?.data?.error?.message || 'Failed to delete user');
    }
  };

  const handleResetPassword = (user: User) => {
    setResetPasswordDialog({
      isOpen: true,
      user,
      newPassword: ''
    });
  };

  const confirmResetPassword = async () => {
    if (!resetPasswordDialog.user || !resetPasswordDialog.newPassword) return;
    
    if (resetPasswordDialog.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }
    
    try {
      await resetPasswordMutation.mutateAsync({
        id: resetPasswordDialog.user.id,
        data: { newPassword: resetPasswordDialog.newPassword }
      });
      toast.success('Password reset successfully');
      setResetPasswordDialog({ isOpen: false, user: null, newPassword: '' });
    } catch (error: any) {
      toast.error(error.response?.data?.error?.message || 'Failed to reset password');
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '—';
    const parsed = new Date(dateString);
    if (Number.isNaN(parsed.getTime())) return '—';
    return parsed.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRoleBadgeColor = (role: string) => {
    const r = normalizeRole(role);
    if (r === 'admin') return 'bg-purple-100 text-purple-800 border-purple-200';
    if (r === 'customer_support') return 'bg-amber-100 text-amber-800 border-amber-200';
    if (r === 'video_recorder') return 'bg-blue-100 text-blue-800 border-blue-200';
    if (r === 'user') return 'bg-gray-100 text-gray-600 border-gray-200';
    return 'bg-gray-100 text-gray-800 border-gray-200';
  };

  // Role management handlers
  const handleAddRole = async () => {
    if (!newRole.name) {
      toast.error('Role name is required');
      return;
    }

    if (newRole.name.length < 2) {
      toast.error('Role name must be at least 2 characters');
      return;
    }

    try {
      await createRoleMutation.mutateAsync(newRole);
      toast.success('Role created successfully');
      setShowAddRoleForm(false);
      setNewRole({ name: '', description: '' });
    } catch (error: any) {
      toast.error(error.response?.data?.error?.message || 'Failed to create role');
    }
  };

  const handleEditRole = (role: Role) => {
    setEditingRoleId(role.id);
    setEditingRole({ ...role });
  };

  const handleSaveRoleEdit = async () => {
    if (!editingRole) return;

    if (editingRole.name && editingRole.name.length < 2) {
      toast.error('Role name must be at least 2 characters');
      return;
    }

    const updateData: UpdateRoleRequest = {
      name: editingRole.name,
      description: editingRole.description || ''
    };

    try {
      await updateRoleMutation.mutateAsync({ id: editingRole.id, data: updateData });
      toast.success('Role updated successfully');
      setEditingRoleId(null);
      setEditingRole(null);
    } catch (error: any) {
      toast.error(error.response?.data?.error?.message || 'Failed to update role');
    }
  };

  const handleCancelRoleEdit = () => {
    setEditingRoleId(null);
    setEditingRole(null);
  };

  const handleDeleteRole = (role: Role) => {
    setDeleteRoleDialog({
      isOpen: true,
      role
    });
  };

  const confirmDeleteRole = async () => {
    if (!deleteRoleDialog.role) return;
    
    try {
      await deleteRoleMutation.mutateAsync(deleteRoleDialog.role.id);
      toast.success('Role deleted successfully');
      setDeleteRoleDialog({ isOpen: false, role: null });
    } catch (error: any) {
      toast.error(error.response?.data?.error?.message || 'Failed to delete role');
    }
  };

  return (
    <div className="etr-page overflow-visible">
      {/* Header */}
      <div className="etr-page-header mb-6">
        {/* Navigation */}
        <div className="mb-4">
          <Link to="/dashboard" className="inline-block">
            <Button variant="ghost" size="sm" leftIcon={<ArrowLeft className="h-4 w-4" />}>
              <span className="hidden sm:inline">Back to Dashboard</span>
              <span className="sm:hidden">Back</span>
            </Button>
          </Link>
        </div>
        {/* Title and Actions */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="etr-page-title flex items-center">
              <UsersIcon className="h-6 w-6 sm:h-8 sm:w-8 mr-2 sm:mr-3 text-blue-600" />
              <span>User Management</span>
            </h1>
            <p className="etr-page-subtitle">Manage system users and their permissions</p>
          </div>
          <Button
            onClick={() => setShowAddForm(true)}
            leftIcon={<Plus className="h-4 w-4" />}
            className="w-full sm:w-auto"
          >
            Add New User
          </Button>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="etr-surface mb-6 p-4 sm:p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="etr-input pl-10 pr-4 py-2.5 text-sm"
            />
          </div>

          {/* Role Filter */}
          <div className="relative overflow-visible z-10">
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              className="etr-input w-full px-4 py-2.5 text-sm appearance-none cursor-pointer relative z-10"
              disabled={rolesLoading}
              style={{ zIndex: 10 }}
            >
              <option value="">All Roles</option>
              {assignableRoles.map((role) => (
                <option key={role.id} value={role.name}>
                  {roleLabel(role.name)}
                </option>
              ))}
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
              <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>

          {/* Clear Filters */}
          {(searchTerm || selectedRole) && (
            <div className="flex items-center">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearchTerm('');
                  setSelectedRole('');
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                Clear Filters
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Add User Form */}
      {showAddForm && (
        <div className="etr-surface mb-6 p-4 sm:p-6 overflow-visible">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Add New User</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
              <input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="user@example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Password *</label>
              <input
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Minimum 6 characters"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">First Name *</label>
              <input
                type="text"
                value={newUser.firstName}
                onChange={(e) => setNewUser({ ...newUser, firstName: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="John"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Last Name *</label>
              <input
                type="text"
                value={newUser.lastName}
                onChange={(e) => setNewUser({ ...newUser, lastName: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Doe"
              />
            </div>
            <div className="relative overflow-visible">
              <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
              <select
                value={newUser.role || ''}
                onChange={(e) => {
                  const role = e.target.value;
                  setNewUser({
                    ...newUser,
                    role,
                    branchIds: isAdmin(role) ? [] : newUser.branchIds ?? [],
                  });
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 relative z-10"
                disabled={rolesLoading}
                style={{ zIndex: 10 }}
              >
                {rolesLoading ? (
                  <option value="">Loading roles...</option>
                ) : (
                  <>
                    <option value="">Select a role</option>
                    {assignableRoles.map((role) => (
                      <option key={role.id} value={role.name}>
                        {roleLabel(role.name)}
                        {role.description && ` - ${role.description}`}
                      </option>
                    ))}
                  </>
                )}
              </select>
            </div>
            {newUser.role && !isAdmin(normalizeRole(newUser.role)) ? (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Branches</label>
                {branchesLoading ? (
                  <p className="text-sm text-gray-500">Loading branches…</p>
                ) : portalBranches.length === 0 ? (
                  <p className="text-sm text-amber-700">No branches available. Ensure ETR.Api is reachable.</p>
                ) : (
                  <div className="flex flex-wrap gap-3">
                    {portalBranches.map((b) => (
                      <label key={b.id} className="inline-flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={(newUser.branchIds ?? []).includes(b.id)}
                          onChange={() =>
                            setNewUser({
                              ...newUser,
                              branchIds: toggleBranchId(newUser.branchIds ?? [], b.id),
                            })
                          }
                        />
                        {b.name}
                      </label>
                    ))}
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-1">Leave unchecked for all branches until you assign specific ones.</p>
              </div>
            ) : newUser.role && isAdmin(normalizeRole(newUser.role)) ? (
              <div className="md:col-span-2 text-sm text-gray-500">Admins have access to all branches.</div>
            ) : null}
          </div>
          <div className="flex justify-end space-x-3 mt-4">
            <Button
              variant="ghost"
              onClick={() => {
                setShowAddForm(false);
                setNewUser({
                  email: '',
                  password: '',
                  firstName: '',
                  lastName: '',
                  role: defaultRoleName,
                  branchIds: [],
                });
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAdd}
              disabled={createMutation.isLoading}
              leftIcon={createMutation.isLoading ? <LoadingSpinner size="sm" /> : <Save className="h-4 w-4" />}
            >
              {createMutation.isLoading ? 'Creating...' : 'Create User'}
            </Button>
          </div>
        </div>
      )}

      {/* Users Table */}
      <div className="etr-surface overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-12 text-red-600">
            <AlertCircle className="h-6 w-6 mr-2" />
            Failed to load users
          </div>
        ) : users.length === 0 ? (
          <div className="flex items-center justify-center py-12 text-gray-500">
            <UsersIcon className="h-12 w-12 mr-4 text-gray-300" />
            <div>
              <h3 className="text-lg font-medium text-gray-900">No users found</h3>
              <p className="text-gray-500">Get started by adding a new user.</p>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto -mx-4 sm:mx-0">
            <div className="inline-block min-w-full align-middle px-4 sm:px-0">
              <table className="etr-table">
              <thead>
                <tr>
                  <th className="px-4 sm:px-6 py-3">
                    User
                  </th>
                  <th className="px-4 sm:px-6 py-3">
                    Role
                  </th>
                  <th className="px-4 sm:px-6 py-3 hidden lg:table-cell">
                    Branches
                  </th>
                  <th className="px-4 sm:px-6 py-3 hidden md:table-cell">
                    Created
                  </th>
                  <th className="px-4 sm:px-6 py-3">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                      {editingId === user.id ? (
                        <div className="space-y-2">
                          <input
                            type="email"
                            value={editingUser?.email || ''}
                            onChange={(e) => setEditingUser({ ...editingUser!, email: e.target.value })}
                            className="etr-input w-full px-3 py-1 text-sm"
                          />
                          <div className="flex space-x-2">
                            <input
                              type="text"
                              value={editingUser?.firstName || ''}
                              onChange={(e) => setEditingUser({ ...editingUser!, firstName: e.target.value })}
                              placeholder="First Name"
                              className="etr-input w-full px-3 py-1 text-sm"
                            />
                            <input
                              type="text"
                              value={editingUser?.lastName || ''}
                              onChange={(e) => setEditingUser({ ...editingUser!, lastName: e.target.value })}
                              placeholder="Last Name"
                              className="etr-input w-full px-3 py-1 text-sm"
                            />
                          </div>
                          {editingUser && !isAdmin(normalizeRole(editingUser.role)) ? (
                            <div className="pt-2">
                              <p className="text-xs font-medium text-gray-600 mb-1">Branches</p>
                              {branchesLoading ? (
                                <p className="text-xs text-gray-500">Loading…</p>
                              ) : portalBranches.length === 0 ? (
                                <p className="text-xs text-amber-700">No branches loaded.</p>
                              ) : (
                                <div className="flex flex-wrap gap-2 max-w-md">
                                  {portalBranches.map((b) => (
                                    <label key={b.id} className="inline-flex items-center gap-1 text-xs text-gray-700">
                                      <input
                                        type="checkbox"
                                        checked={(editingUser.branchIds ?? []).includes(b.id)}
                                        onChange={() =>
                                          setEditingUser({
                                            ...editingUser,
                                            branchIds: toggleBranchId(editingUser.branchIds ?? [], b.id),
                                          })
                                        }
                                      />
                                      {b.name}
                                    </label>
                                  ))}
                                </div>
                              )}
                            </div>
                          ) : editingUser && isAdmin(normalizeRole(editingUser.role)) ? (
                            <p className="text-xs text-gray-500 pt-1">All branches (admin)</p>
                          ) : null}
                        </div>
                      ) : (
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {user.firstName} {user.lastName}
                          </div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </div>
                      )}
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                      {editingId === user.id ? (
                        <select
                          value={editingUser?.role || ''}
                          onChange={(e) => {
                            const role = e.target.value;
                            setEditingUser({
                              ...editingUser!,
                              role,
                              branchIds: isAdmin(role) ? [] : editingUser?.branchIds ?? [],
                            });
                          }}
                          className="etr-input px-3 py-1 text-sm"
                          disabled={rolesLoading}
                        >
                          {rolesLoading ? (
                            <option value="">Loading...</option>
                          ) : (
                            assignableRoles.map((role) => (
                              <option key={role.id} value={role.name}>
                                {roleLabel(role.name)}
                              </option>
                            ))
                          )}
                        </select>
                      ) : (
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getRoleBadgeColor(user.role)}`}>
                          {roleLabel(user.role)}
                        </span>
                      )}
                    </td>
                    <td className="px-4 sm:px-6 py-4 text-sm text-gray-600 hidden lg:table-cell max-w-xs">
                      {editingId === user.id ? (
                        <span className="text-gray-400 text-xs">Edit branches in user column →</span>
                      ) : (
                        <span className="line-clamp-2">{formatBranchSummary(user.branchIds ?? [])}</span>
                      )}
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500 hidden md:table-cell">
                      {formatDate(user.createdAt)}
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm font-medium">
                      {editingId === user.id ? (
                        <div className="flex flex-wrap gap-1 sm:gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleSaveEdit}
                            disabled={updateMutation.isLoading}
                            leftIcon={updateMutation.isLoading ? <LoadingSpinner size="sm" /> : <Save className="h-3 w-3" />}
                            className="text-green-600 hover:text-green-700"
                          >
                            <span className="hidden sm:inline">{updateMutation.isLoading ? 'Saving...' : 'Save'}</span>
                            <span className="sm:hidden">{updateMutation.isLoading ? '...' : '✓'}</span>
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleCancelEdit}
                            leftIcon={<X className="h-3 w-3" />}
                            className="text-gray-600 hover:text-gray-700"
                            title="Cancel"
                          >
                            <span className="hidden sm:inline">Cancel</span>
                          </Button>
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-1 sm:gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(user)}
                            leftIcon={<Edit2 className="h-3 w-3" />}
                            className="text-blue-600 hover:text-blue-700"
                            title="Edit"
                          >
                            <span className="hidden sm:inline">Edit</span>
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleResetPassword(user)}
                            leftIcon={<Key className="h-3 w-3" />}
                            className="text-orange-600 hover:text-orange-700"
                            title="Reset password"
                          >
                            <span className="hidden sm:inline">Reset Password</span>
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(user)}
                            leftIcon={<Trash2 className="h-3 w-3" />}
                            className="text-red-600 hover:text-red-700"
                            title="Delete user"
                          >
                            <span className="hidden sm:inline">Delete</span>
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Role Management Section */}
      <div className="mt-8">
        <div 
          className="bg-white rounded-lg shadow-sm border border-gray-200 cursor-pointer hover:border-blue-300 transition-colors"
          onClick={() => setShowRolesSection(!showRolesSection)}
        >
          <div className="p-6 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Shield className="h-6 w-6 text-purple-600" />
              <div>
                <h2 className="text-xl font-bold text-gray-900">Role Management</h2>
                <p className="text-sm text-gray-600 mt-0.5">Create, edit, and delete user roles</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setShowRolesSection(!showRolesSection);
              }}
            >
              {showRolesSection ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
            </Button>
          </div>
        </div>

        {showRolesSection && (
          <div className="mt-4 bg-white rounded-lg shadow-sm border border-gray-200">
            {/* Role Search */}
            <div className="p-6 border-b border-gray-200">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search roles..."
                  value={roleSearchTerm}
                  onChange={(e) => setRoleSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors duration-200"
                />
              </div>
            </div>

            {/* Add Role Form */}
            {showAddRoleForm && (
              <div className="p-6 border-b border-gray-200 bg-gray-50">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Add New Role</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Role Name *</label>
                    <input
                      type="text"
                      value={newRole.name}
                      onChange={(e) => setNewRole({ ...newRole, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      placeholder="e.g., moderator"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                    <input
                      type="text"
                      value={newRole.description || ''}
                      onChange={(e) => setNewRole({ ...newRole, description: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      placeholder="Role description (optional)"
                      maxLength={200}
                    />
                  </div>
                </div>
                <div className="flex justify-end space-x-3 mt-4">
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setShowAddRoleForm(false);
                      setNewRole({ name: '', description: '' });
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleAddRole}
                    disabled={createRoleMutation.isLoading}
                    leftIcon={createRoleMutation.isLoading ? <LoadingSpinner size="sm" /> : <Save className="h-4 w-4" />}
                  >
                    {createRoleMutation.isLoading ? 'Creating...' : 'Create Role'}
                  </Button>
                </div>
              </div>
            )}

            {/* Roles Table */}
            <div className="overflow-x-auto">
              {rolesLoading ? (
                <div className="flex items-center justify-center py-12">
                  <LoadingSpinner size="lg" />
                </div>
              ) : allRoles.length === 0 ? (
                <div className="flex items-center justify-center py-12 text-gray-500">
                  <Shield className="h-12 w-12 mr-4 text-gray-300" />
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">No roles found</h3>
                    <p className="text-gray-500">Get started by adding a new role.</p>
                  </div>
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Role Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Description
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {(allRoles.length > 0 ? allRoles : assignableRoles).map((role) => (
                      <tr key={role.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          {editingRoleId === role.id ? (
                            <input
                              type="text"
                              value={editingRole?.name || ''}
                              onChange={(e) => setEditingRole({ ...editingRole!, name: e.target.value })}
                              className="px-3 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-purple-500"
                            />
                          ) : (
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getRoleBadgeColor(role.name)}`}>
                              {roleLabel(role.name)}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {editingRoleId === role.id ? (
                            <input
                              type="text"
                              value={editingRole?.description || ''}
                              onChange={(e) => setEditingRole({ ...editingRole!, description: e.target.value })}
                              className="w-full px-3 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-purple-500"
                              maxLength={200}
                            />
                          ) : (
                            <span className="text-sm text-gray-500">{role.description || 'No description'}</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(role.createdAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-right">
                          {editingRoleId === role.id ? (
                            <div className="flex justify-end space-x-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleSaveRoleEdit}
                                disabled={updateRoleMutation.isLoading}
                                leftIcon={updateRoleMutation.isLoading ? <LoadingSpinner size="sm" /> : <Save className="h-3 w-3" />}
                                className="text-green-600 hover:text-green-700"
                              >
                                {updateRoleMutation.isLoading ? 'Saving...' : 'Save'}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleCancelRoleEdit}
                                leftIcon={<X className="h-3 w-3" />}
                                className="text-gray-600 hover:text-gray-700"
                              >
                                Cancel
                              </Button>
                            </div>
                          ) : (
                            <div className="flex justify-end space-x-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditRole(role)}
                                leftIcon={<Edit2 className="h-3 w-3" />}
                                className="text-blue-600 hover:text-blue-700"
                              >
                                Edit
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteRole(role)}
                                leftIcon={<Trash2 className="h-3 w-3" />}
                                className="text-red-600 hover:text-red-700"
                              >
                                Delete
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Add Role Button */}
            {!showAddRoleForm && (
              <div className="p-6 border-t border-gray-200 bg-gray-50">
                <Button
                  onClick={() => setShowAddRoleForm(true)}
                  leftIcon={<Plus className="h-4 w-4" />}
                >
                  Add New Role
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Info Section */}
      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start">
          <AlertCircle className="h-5 w-5 text-blue-500 mr-3 mt-0.5" />
          <div>
            <h4 className="font-medium text-blue-900">User Management</h4>
            <p className="mt-1 text-sm text-blue-700">
              Manage system users, their roles, and permissions. Admins can create, edit, and delete users, 
              as well as reset passwords. Users with existing sessions cannot be deleted.
            </p>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={deleteDialog.isOpen}
        onClose={() => setDeleteDialog({ isOpen: false, user: null })}
        onConfirm={confirmDelete}
        title="Delete User"
        message={
          deleteDialog.user
            ? `Are you sure you want to delete "${deleteDialog.user?.firstName || ''} ${deleteDialog.user?.lastName || ''}"? This action cannot be undone.`
            : ''
        }
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={deleteMutation.isLoading}
      />

      {/* Reset Password Dialog */}
      <ConfirmationDialog
        isOpen={resetPasswordDialog.isOpen}
        onClose={() => setResetPasswordDialog({ isOpen: false, user: null, newPassword: '' })}
        onConfirm={confirmResetPassword}
        title="Reset Password"
        message={
          resetPasswordDialog.user
            ? `Enter a new password for "${resetPasswordDialog.user?.firstName || ''} ${resetPasswordDialog.user?.lastName || ''}":`
            : ''
        }
        confirmText="Reset Password"
        cancelText="Cancel"
        variant="warning"
        isLoading={resetPasswordMutation.isLoading}
        customContent={
          resetPasswordDialog.isOpen && (
            <div className="mt-4">
              <input
                type="password"
                value={resetPasswordDialog.newPassword}
                onChange={(e) => setResetPasswordDialog({ ...resetPasswordDialog, newPassword: e.target.value })}
                placeholder="New password (minimum 6 characters)"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                autoFocus
              />
            </div>
          )
        }
      />

      {/* Delete Role Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={deleteRoleDialog.isOpen}
        onClose={() => setDeleteRoleDialog({ isOpen: false, role: null })}
        onConfirm={confirmDeleteRole}
        title="Delete Role"
        message={
          deleteRoleDialog.role
            ? `Are you sure you want to delete the role "${deleteRoleDialog.role?.name || ''}"? This action cannot be undone. If users are assigned to this role, you'll need to reassign them first.`
            : ''
        }
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={deleteRoleMutation.isLoading}
      />
    </div>
  );
};

export default Users;
